import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Strike from '@tiptap/extension-strike';
import Code from '@tiptap/extension-code';
import CodeBlock from '@tiptap/extension-code-block';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import FontFamily from '@tiptap/extension-font-family';
import FontSize from '@tiptap/extension-font-size';
import { Node } from '@tiptap/core';
import {
  AlignLeft,
  AlignCenter,
  AlignRight,
  Image as LucideImage,
  Video as LucideVideo,
  Link as LucideLink
} from 'lucide-react';
import './addNote.css';
import { useDispatch, useSelector } from 'react-redux';
import { getNotesforReports, saveNotesforReports } from '../../../apiServices';
import { CustomSuccessModal } from 'components';

// Custom Iframe Extension for Video Embeds
const Iframe = Node.create({
  name: 'iframe',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      src: {
        default: null,
      },
      title: {
        default: null,
      },
      frameborder: {
        default: '0',
      },
      allowfullscreen: {
        default: true,
      },
      allow: {
        default: 'autoplay; encrypted-media; picture-in-picture',
      },
      style: {
        default: 'width: 100%; height: 315px; max-width: 560px; margin: 10px 0; border-radius: 4px;',
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'iframe',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ['iframe', HTMLAttributes]
  },

  addCommands() {
    return {
      setIframe: (options) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        })
      },
    }
  },
});

const AddNoteDialog = ({ show, onHide, onSave, onNotes, initialNotes = [], user_id, report_id, token, reportName, isSharedReport }) => {
  const [notes, setNotes] = useState('');
  const [noteDataList, setNoteDataList] = useState();
  const [selectedNote, setSelectedNote] = useState(null);

  const userData = useSelector((state) => state.apiService.login);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const dispatch = useDispatch();
  const currentUserId = userData?.user_id;

  // For shared reports, use original user's ID to fetch notes, otherwise use current user's ID
  const notesUserId = isSharedReport ? user_id : currentUserId;

  const params = {
    UserID: notesUserId,
    ReportID: report_id,
    token: token
  }

  // Clear selection when user starts typing in editor
  const handleEditorUpdate = useCallback(({ editor }) => {
    const html = editor.getHTML();
    setNotes(html);

    // If user is typing and there's a selected note, deselect it
    if (selectedNote && html !== selectedNote.notes) {
      setSelectedNote(null);
      if (onNotes) onNotes(null);
    }
  }, [selectedNote, onNotes]);

  // TipTap Editor configuration
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Underline,
      Strike,
      Code,
      CodeBlock,
      Subscript,
      Superscript,
      TextStyle,
      Color,
      Highlight,
      Link,
      Image.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: {
          class: 'tiptap-image',
          style: 'max-width: 100%; height: auto; display: block; margin: 10px 0; border-radius: 4px;',
        },
        parseHTML() {
          return [
            {
              tag: 'img[src]',
            },
          ]
        },
        renderHTML({ HTMLAttributes }) {
          return ['img', HTMLAttributes]
        },
      }),
      Iframe,
      Placeholder.configure({ placeholder: 'Write your note...' }),
      FontFamily,
      FontSize.configure({
        types: ['textStyle'],
      }),
    ],
    content: notes,
    onUpdate: handleEditorUpdate,
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
      },
    },
  });

  const imageInputRef = useRef();

  // Handler for file input change
  const handleImageUpload = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type.toLowerCase())) {
      alert('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert('Image size should be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;

      if (editor && !editor.isDestroyed) {
        try {
          editor.commands.focus();

          const imgHtml = `<img src="${dataUrl}" alt="${file.name}" title="${file.name}" style="max-width: 100%; height: auto; display: block; margin: 10px 0; border-radius: 4px;" />`;

          let success = false;

          // Method 1: Direct HTML insertion
          try {
            success = editor.chain().focus().insertContent(imgHtml).run();
          } catch (error) {
            console.log('Direct HTML insertion failed:', error);
          }

          // Method 2: Insert as paragraph with image
          if (!success) {
            try {
              const paragraphHtml = `<p>${imgHtml}</p>`;
              success = editor.chain().focus().insertContent(paragraphHtml).run();
            } catch (error) {
              console.log('Paragraph insertion failed:', error);
            }
          }

          if (success) {
            const updatedContent = editor.getHTML();
            setNotes(updatedContent);
          } else {
            alert('Failed to insert image. Please try again.');
          }

        } catch (error) {
          console.error('Error inserting image:', error);
          alert('Failed to insert image. Please try again.');
        }
      }
    };

    reader.onerror = (error) => {
      console.error('Error reading image file:', error);
      alert('Error reading image file');
    };

    reader.readAsDataURL(file);

    // Reset input value so the same file can be selected again
    event.target.value = '';
  };

  // Toolbar component
  const Toolbar = () => {
    if (!editor) return null;

    const handleFormattingClick = (formatType) => {
      switch (formatType) {
        case 'bold':
          editor.chain().focus().toggleBold().run();
          break;
        case 'italic':
          editor.chain().focus().toggleItalic().run();
          break;
        case 'underline':
          editor.chain().focus().toggleUnderline().run();
          break;
        case 'code':
          editor.chain().focus().toggleCode().run();
          break;
        default:
          break;
      }
    };

    const handleAlignmentClick = (alignment) => {
      editor.chain().focus().setTextAlign(alignment).run();
    };

    return (
      <div className="tiptap-toolbar exact-toolbar" style={{ overflow: 'visible' }}>
        {/* Bold */}
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleFormattingClick('bold')}
          className={`toolbar-button ${editor.isActive('bold') ? 'is-active' : ''}`}
          title="Bold"
        >
          <span style={{ fontWeight: 'bold', fontSize: '16px' }}>B</span>
        </button>

        {/* Italic */}
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleFormattingClick('italic')}
          className={`toolbar-button ${editor.isActive('italic') ? 'is-active' : ''}`}
          title="Italic"
        >
          <span style={{ fontStyle: 'italic', fontSize: '16px' }}>I</span>
        </button>

        {/* Underline */}
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleFormattingClick('underline')}
          className={`toolbar-button ${editor.isActive('underline') ? 'is-active' : ''}`}
          title="Underline"
        >
          <span style={{ textDecoration: 'underline', fontSize: '16px' }}>U</span>
        </button>

        <div className="toolbar-divider"></div>

        {/* Text Alignment */}
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleAlignmentClick('left')}
          className={`toolbar-button ${editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}`}
          title="Align Left"
        >
          <AlignLeft size={16} />
        </button>
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleAlignmentClick('center')}
          className={`toolbar-button ${editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}`}
          title="Align Center"
        >
          <AlignCenter size={16} />
        </button>
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleAlignmentClick('right')}
          className={`toolbar-button ${editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}`}
          title="Align Right"
        >
          <AlignRight size={16} />
        </button>

        <div className="toolbar-divider"></div>

        {/* Link */}
        {/* <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              editor.chain().focus();
              const previousUrl = editor.getAttributes('link').href || '';
              const url = window.prompt('Enter URL', previousUrl);
              if (url === null) return;
              if (url === '') {
                editor.extendMarkRange('link').unsetLink().run();
                return;
              }
              if (editor.state.selection.empty) {
                editor.insertContent(`<a href=\"${url}\">${url}</a>`).run();
              } else {
                editor.extendMarkRange('link').setLink({ href: url }).run();
              }
            }}
            className={`toolbar-button ${editor.isActive('link') ? 'is-active' : ''}`}
            title="Link"
          >
            <LucideLink size={16} />
          </button> */}

        {/* Image upload button */}
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            editor.chain().focus();
            const fileInput = document.createElement('input');
            fileInput.type = 'file';
            fileInput.accept = 'image/*';
            fileInput.style.display = 'none';

            fileInput.onchange = (event) => {
              handleImageUpload(event);
              document.body.removeChild(fileInput);
            };

            document.body.appendChild(fileInput);
            fileInput.click();
          }}
          className="toolbar-button"
          title="Upload Image"
        >
          <LucideImage size={16} />
        </button>

        <div className="toolbar-divider"></div>

        {/* Video */}
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            editor.chain().focus();
            const videoUrl = window.prompt('Enter video URL (YouTube, Vimeo, etc.)');
            if (videoUrl) {
              // Convert to embed URL
              let embedUrl = videoUrl;
              const youtubeMatch = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
              if (youtubeMatch) {
                embedUrl = `https://www.youtube.com/embed/${youtubeMatch[1]}`;
              }
              const vimeoMatch = videoUrl.match(/vimeo\.com\/(\d+)/);
              if (vimeoMatch) {
                embedUrl = `https://player.vimeo.com/video/${vimeoMatch[1]}`;
              }

              const iframeData = {
                src: embedUrl,
                title: 'Video',
                frameborder: '0',
                allowfullscreen: true,
                allow: 'autoplay; encrypted-media; picture-in-picture',
                style: 'width: 100%; height: 315px; max-width: 560px; margin: 10px 0; border-radius: 4px;'
              };

              editor.chain().focus().setIframe(iframeData).run();
            }
          }}
          className="toolbar-button"
          title="Insert Video"
        >
          <LucideVideo size={16} />
        </button>

        {/* Code */}
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleFormattingClick('code')}
          className={`toolbar-button ${editor.isActive('code') ? 'is-active' : ''}`}
          title="Inline Code"
        >
          {'<>'}
        </button>

        <input
          type="file"
          accept="image/*"
          ref={imageInputRef}
          style={{ display: 'none' }}
          onChange={handleImageUpload}
        />
      </div>
    );
  };

  useEffect(() => {
    if (show && report_id) {
      console.log("AddNoteDialog Debug Info:", {
        isSharedReport,
        currentUserId,
        user_id,
        report_id,
        show,
        notesUserId
      });

      dispatch(getNotesforReports(params)).then((res) => {
        console.log("Notes API response:", res);
        if (res.payload && res.payload.Table && res.payload.Table.length > 0) {
          setNoteDataList(res.payload.Table);
        } else {
          setNoteDataList([]);
        }
      }).catch((error) => {
        console.error("Error fetching notes:", error);
        setNoteDataList([]);
      });
    }
  }, [show, report_id]);

  const handleSave = () => {
    // Additional validation to prevent saving empty notes
    if (isContentEmpty(notes)) {
      return;
    }
    
    const paramsforSaveNotes = {
      loginUserId: currentUserId, // Always use current user's ID when saving
      reportId: report_id,
      token: token,
      notes: notes,
      isShared: isSharedReport || false
    }
    dispatch(saveNotesforReports(paramsforSaveNotes)).then((res) => {
      if (res.payload) {
        setNotes('');
        if (editor) {
          editor.chain().focus().clearContent().run();
        }
        setShowSuccessModal(true);
        // Refresh notes after saving - use the same logic as initial fetch
        const refreshParams = {
          UserID: notesUserId,
          ReportID: report_id,
          token: token
        };

        dispatch(getNotesforReports(refreshParams)).then((res) => {
          if (res.payload && res.payload.Table) {
            setNoteDataList(res.payload.Table);
          } else {
            setNoteDataList([]);
          }
        }).catch((error) => {
          console.error("Error refreshing notes:", error);
          setNoteDataList([]);
        });
      }
    }).catch((error) => {
      console.error("Error saving note:", error);
    });
  };

  const handleNoteClick = (note) => {
    // If the same note is already selected, deselect it
    if (selectedNote && selectedNote.id === note.id) {
      setSelectedNote(null);
      setNotes('');
      if (editor) {
        editor.chain().focus().clearContent().run();
      }
      // Call onNotes callback with null to indicate deselection
      if (onNotes) onNotes(null);
    } else {
      // Select the new note for viewing only (no editing)
      setSelectedNote(note);
      // Clear the editor for new note creation instead of loading existing note
      setNotes('');
      if (editor) {
        editor.chain().focus().clearContent().run();
      }
      // Call onNotes callback if it exists
      if (onNotes) onNotes(note);
    }
  };

  // Function to check if HTML content is empty or contains only whitespace
  const isContentEmpty = (htmlContent) => {
    if (!htmlContent) return true;
    
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    // Get text content and trim whitespace
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    const trimmedText = textContent.trim();
    
    // Check if there's any actual text content
    return trimmedText.length === 0;
  };

  const getNotePreview = (noteText) => {
    const maxLength = 50;
    if (noteText && noteText.length > maxLength) {
      return noteText.slice(0, maxLength) + '...';
    } else {
      return noteText;
    }
  };

  if (!show) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header-add-note">
          <h5 className="modal-title-add-note">Add Note for <b className="add-note-report-name">{reportName || 'Report'}</b></h5>
          <button className="modal-close" onClick={onHide}>×</button>
        </div>
        <div className="modal-body-add-note">
          <div className="note-layout">
            <div className="create-note-section">
              <h6 className="section-title">Create Note</h6>
              <div className="editor-container">
                <div className="tiptap-editor" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Toolbar />
                  <div style={{ flex: 1, overflow: 'auto', minHeight: '200px' }}>
                    <EditorContent editor={editor} />
                  </div>
                </div>
              </div>
              <div className="editor-footer">
                <button
                  className="modal-button clear-button"
                  onClick={() => {
                    setNotes('');
                    if (editor) {
                      editor.chain().focus().clearContent().run();
                    }
                  }}
                >
                  Clear
                </button>
                <button
                  className="modal-button save-button"
                  onClick={handleSave}
                  disabled={isContentEmpty(notes)}
                >
                  Save
                </button>
              </div>
            </div>
            <div className="notes-section">
              <h6 className="section-title">Notes</h6>
              <div className="notes-list">
                {noteDataList !== undefined && noteDataList.map((note) => (
                  <div
                    key={note.id}
                    className={`list-group-item note-item ${selectedNote?.id === note.id ? 'expanded' : ''}`}
                    onClick={() => handleNoteClick(note)}
                    style={{ cursor: 'pointer' }}
                    title="Click to view note (read-only)"
                  >
                    <div className="d-flex w-100 justify-content-between" style={{ minHeight: '10px' }}>
                      <div className="d-flex align-items-center">
                        <div className="profile-circle me-2">
                          {note.image_path ? (
                            <img
                              className="profile-circle"
                              src={note.image_path}
                              alt="User Profile"
                              onError={() => note.image_path = null}
                            />
                          ) : (
                            <span>{note.created_user_name ? note.created_user_name.charAt(0).toUpperCase() : 'U'}</span>
                          )}
                        </div>
                        <h6 className="mb-0 note-author">{note.created_user_name || 'User'}</h6>
                      </div>
                      <small className="note-date">{note.created_on}</small>
                    </div>
                    <p className="mb-1 note-text" dangerouslySetInnerHTML={{
                      __html: selectedNote?.id === note.id ? note.notes : getNotePreview(note.notes)
                    }}></p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <CustomSuccessModal
        show={showSuccessModal}
        onHide={() => {
          setShowSuccessModal(false);
        }}
        title="Note Saved"
        message="Note has been saved successfully"
      />
    </div>
  );
};

export default AddNoteDialog;


